import { z } from 'zod';
/**
 * What a session is allowed to do. Limits live on the session row rather than
 * behind an `if (demo)` branch, so a further plan is a new value here plus the
 * limits to match - no new code at the places that enforce them.
 */
export const SESSION_PLANS = ['demo', 'unpaid', 'pro'];
/** The terms of a demo wall, quoted in the copy and applied by the server. */
export const DEMO_TTL_MINUTES = 5;
export const DEMO_MEDIA_LIMIT = 5;
export const DEMO_MESSAGE_LIMIT = 5;
export const TRANSITIONS = ['fade', 'slide', 'kenburns'];
export const QR_POSITIONS = [
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
    'hidden',
];
export const QR_SIZES = ['sm', 'md', 'lg'];
export const TICKER_SPEEDS = ['slow', 'normal', 'fast'];
export const MIN_SLIDE_DURATION_MS = 2_000;
export const MAX_SLIDE_DURATION_MS = 60_000;
export const MAX_VIDEO_SECONDS_CAP = 60;
export const MIN_TICKER_LIMIT = 5;
export const MAX_TICKER_LIMIT = 200;
/** How fast the ticker travels, in CSS pixels per second. */
export const TICKER_PX_PER_SECOND = {
    slow: 60,
    normal: 100,
    fast: 150,
};
/** Everything the host can tune. Every field has a default, so PATCH is partial. */
export const sessionSettingsSchema = z.object({
    slideDurationMs: z
        .number()
        .int()
        .min(MIN_SLIDE_DURATION_MS)
        .max(MAX_SLIDE_DURATION_MS)
        .default(8_000),
    transition: z.enum(TRANSITIONS).default('fade'),
    shuffle: z.boolean().default(false),
    newMediaPriority: z.boolean().default(true),
    qrPosition: z.enum(QR_POSITIONS).default('bottom-right'),
    qrPrompt: z.string().max(120).default('Scan to add your photos!'),
    qrSize: z.enum(QR_SIZES).default('md'),
    showCaptions: z.boolean().default(true),
    allowVideo: z.boolean().default(true),
    maxVideoSeconds: z.number().int().min(1).max(MAX_VIDEO_SECONDS_CAP).default(10),
    allowMessages: z.boolean().default(true),
    showTicker: z.boolean().default(true),
    tickerSpeed: z.enum(TICKER_SPEEDS).default('normal'),
    /**
     * How many recent messages one loop of the ticker carries. A long party can
     * produce hundreds, and a loop that long feels like nothing is moving.
     */
    tickerLimit: z
        .number()
        .int()
        .min(MIN_TICKER_LIMIT)
        .max(MAX_TICKER_LIMIT)
        .default(40),
});
/** A PATCH body: any subset of the settings, but at least one field. */
export const sessionSettingsPatchSchema = sessionSettingsSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'No settings provided' });
/** A demo wall needs no account, and even the name is optional. */
export const createDemoSessionSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
});
/** How much of an allowance is left. Null stays null: unlimited. */
export function remainingAllowance(limit, used) {
    return limit === null ? null : Math.max(0, limit - used);
}
export function defaultSessionSettings() {
    return sessionSettingsSchema.parse({});
}
//# sourceMappingURL=session.js.map